import { QueryFilters, useQuery, useQueryClient } from '@tanstack/react-query';
import { first, get, isEmpty, some } from 'es-toolkit/compat';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getDefaultManifestDialogUrlState, ManifestDialogUrlState } from './dialogState';
import { ManifestError, readManifestWorkbookContext } from './helpers';
import { loadManifestDestination, ManifestDestination } from './loadManifestDestination';
import { getManifestDialogUrl, ManifestDestinationNavigationState } from './manifestDialogUrl';
import { replaceManifestDraftFromUpload, resolveManifestFileRows } from './manifestFileImport';
import { UploadedDuplicateSlideRow, UploadedManifestResolution } from './manifestResolution';
import { ManifestWorkbookContextResult } from './manifestWorkbookContext';

const droppedUploadMessage =
  'Your workbook was not applied because the case assignment changed while it was being read. Choose the file again to import it.';

interface ManifestFileImportInput
  extends Omit<Parameters<typeof resolveManifestFileRows>[0], 'file' | 'isCurrentUpload'> {
  open: boolean;
  executionMode: string;
  canIncludeAssigned: boolean;
  canSwitchLabs: boolean;
  draftUploadScope: string;
  uploadedErrorMessages: string[];
  sourcesLoading: boolean;
  hasSourceError: boolean;
  updateManifestDialogUrlState: (
    updates: Partial<ManifestDialogUrlState> | ((previousState: ManifestDialogUrlState) => ManifestDialogUrlState)
  ) => void;
  onDraftAccepted: () => void;
  onBeforeHandlingChange: () => void;
}

export const useManifestFileImport = ({
  open,
  labId,
  studyId,
  executionMode,
  canIncludeAssigned,
  canSwitchLabs,
  draftUploadScope,
  uploadedErrorMessages,
  sourcesLoading,
  hasSourceError,
  existingProcedures,
  editableExistingAndMockCases,
  pendingCaseCount,
  procedureFieldContext,
  fields,
  rowsPerPage,
  fetchSlidesByIdBatches,
  updateManifestDialogUrlState,
  onDraftAccepted,
  onBeforeHandlingChange,
}: ManifestFileImportInput) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigationState = location.state as ManifestDestinationNavigationState | null;
  const destinationTransfer = navigationState?.manifestDestination;
  const claimedNavigationFile = useRef<File | undefined>();
  const manifestInputRef = useRef<HTMLInputElement | null>(null);
  const closeFileMenuRef = useRef<(() => void) | null>(null);
  const [resolvingUpload, setResolvingUpload] = useState<{ requestId: number; scope: string } | null>(null);
  const [pendingCasePlanFix, setPendingCasePlanFix] = useState<UploadedManifestResolution | null>(null);
  const [pendingDuplicateUpload, setPendingDuplicateUpload] = useState<{
    file: File;
    rows: UploadedDuplicateSlideRow[];
    requestId: number;
    scope: string;
  } | null>(null);
  const exportedPlanSignature = useRef<string | null>(null);
  const [pendingOverwrite, setPendingOverwrite] = useState<{
    file: File;
    requestId: number;
    scope: string;
  } | null>(null);
  const [overwriteAccepted, setOverwriteAccepted] = useState(false);
  const uploadRequestId = useRef(0);
  const uploadScope = JSON.stringify([
    open,
    labId,
    studyId,
    executionMode,
    canIncludeAssigned,
    canSwitchLabs,
    draftUploadScope,
  ]);
  const currentUploadScope = useRef(uploadScope);
  // Requests ended by a plan change (not by the user dismissing or replacing the import) must be reported, not dropped.
  const requestIdsEndedByScopeChange = useRef(new Set<number>());
  if (currentUploadScope.current !== uploadScope) {
    requestIdsEndedByScopeChange.current.add(uploadRequestId.current);
    uploadRequestId.current += 1;
    currentUploadScope.current = uploadScope;
  }
  const isResolvingUploadedManifest =
    resolvingUpload?.requestId === uploadRequestId.current && resolvingUpload.scope === uploadScope;
  const [deferredUpload, setDeferredUpload] = useState<{ file: File; requestId: number; scope: string } | null>(null);
  const [pendingWorkbookContext, setPendingWorkbookContext] = useState<{
    file: File;
    result: Extract<ManifestWorkbookContextResult, { status: 'valid' }>;
    requestId: number;
    scope: string;
    overwriteAccepted: boolean;
  } | null>(null);
  const activeWorkbookContext =
    pendingWorkbookContext?.requestId === uploadRequestId.current && pendingWorkbookContext.scope === uploadScope
      ? pendingWorkbookContext
      : null;
  const foreignContext =
    activeWorkbookContext &&
    (activeWorkbookContext.result.context.studyId !== studyId || activeWorkbookContext.result.context.labId !== labId)
      ? activeWorkbookContext.result.context
      : null;
  const destinationLookup = useQuery({
    queryKey: [
      'manifestDestination',
      uploadScope,
      activeWorkbookContext?.requestId,
      foreignContext?.studyId,
      foreignContext?.labId,
    ],
    queryFn: ({ signal }) =>
      loadManifestDestination({ ...foreignContext!, currentLabId: labId, canSwitchLabs }, signal),
    enabled: Boolean(foreignContext && canIncludeAssigned && (foreignContext.labId === labId || canSwitchLabs)),
    retry: false,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
  });
  const [destinationConfirmation, setDestinationConfirmation] = useState<{
    requestId: number;
    scope: string;
    destination: ManifestDestination;
  } | null>(null);
  const activeDestinationConfirmation =
    destinationConfirmation?.requestId === uploadRequestId.current && destinationConfirmation.scope === uploadScope
      ? destinationConfirmation
      : null;
  const [destinationError, setDestinationError] = useState<{ scope: string; message: string } | null>(null);
  const [isReturningToOriginal, setIsReturningToOriginal] = useState(false);
  const planSignature = JSON.stringify([labId, studyId, draftUploadScope]);
  const currentPlanSignature = useRef(planSignature);
  currentPlanSignature.current = planSignature;
  const activePendingOverwrite =
    pendingOverwrite?.requestId === uploadRequestId.current && pendingOverwrite.scope === uploadScope
      ? pendingOverwrite
      : null;
  // Its context and overwrite confirmations belong to the scope it was read in, so a changed scope retires it.
  const activePendingDuplicateUpload =
    pendingDuplicateUpload?.requestId === uploadRequestId.current && pendingDuplicateUpload.scope === uploadScope
      ? pendingDuplicateUpload
      : null;
  const disableInput = sourcesLoading || isResolvingUploadedManifest;
  const destinationSourcesLoading =
    disableInput ||
    some([
      procedureFieldContext.cancerTypesLoading,
      procedureFieldContext.allCancerTypesLoading,
      procedureFieldContext.stainTypesInStudyLoading,
      procedureFieldContext.studiesLoading,
    ]);

  const reportDroppedUpload = (requestId: number, requestScope: string) => {
    if (!requestIdsEndedByScopeChange.current.delete(requestId)) return;
    const [wasOpen, requestLabId, requestStudyId] = JSON.parse(requestScope);
    const [isOpen, currentLabId, currentStudyId] = JSON.parse(currentUploadScope.current);
    // Closing the dialog or switching lab/study is the user leaving the plan, so there is nothing to report there.
    if (!wasOpen || !isOpen || requestLabId !== currentLabId || requestStudyId !== currentStudyId) return;
    updateManifestDialogUrlState({ uploadedErrorMessages: [droppedUploadMessage] });
  };

  const invalidateUpload = () => {
    uploadRequestId.current += 1;
    setPendingWorkbookContext(null);
    setResolvingUpload(null);
  };

  const dismissFileImport = () => {
    uploadRequestId.current += 1;
    setPendingOverwrite(null);
    setOverwriteAccepted(false);
    setPendingWorkbookContext(null);
    setPendingCasePlanFix(null);
    setPendingDuplicateUpload(null);
    setResolvingUpload(null);
    setDestinationConfirmation(null);
    if (uploadedErrorMessages.length) {
      updateManifestDialogUrlState({ uploadedErrorMessages: [] });
    }
  };

  useEffect(
    () => () => {
      uploadRequestId.current += 1;
    },
    [open, labId, studyId]
  );

  const acceptResolvedManifestUpload = (resolvedUploadState: UploadedManifestResolution) => {
    const noManifestRowsGenerated = isEmpty(resolvedUploadState.manifestCases);
    updateManifestDialogUrlState((previousState) =>
      replaceManifestDraftFromUpload({ previousState, resolution: resolvedUploadState, existingProcedures })
    );
    setPendingCasePlanFix(null);

    if (!noManifestRowsGenerated) {
      exportedPlanSignature.current = null;
      onDraftAccepted();
    }
  };

  const importManifestFile = async (
    file: File,
    contextReconciled = false,
    acceptOverwrite = false,
    excludedRowNumbers: number[] = []
  ) => {
    const requestId = ++uploadRequestId.current;
    const requestScope = currentUploadScope.current;
    const isCurrentUpload = () => requestId === uploadRequestId.current && requestScope === currentUploadScope.current;
    setPendingWorkbookContext(null);
    setPendingOverwrite(null);
    setPendingCasePlanFix(null);
    setPendingDuplicateUpload(null);
    setOverwriteAccepted(false);
    setResolvingUpload({ requestId, scope: requestScope });

    try {
      if (
        exportedPlanSignature.current &&
        exportedPlanSignature.current !== currentPlanSignature.current &&
        !acceptOverwrite
      ) {
        setPendingOverwrite({ file, requestId, scope: requestScope });
        return;
      }
      if (!contextReconciled) {
        const result = await readManifestWorkbookContext(file);
        if (!isCurrentUpload()) return reportDroppedUpload(requestId, requestScope);
        if (
          result.status === 'valid' &&
          (result.context.studyId !== studyId ||
            result.context.labId !== labId ||
            result.context.handling !== executionMode)
        ) {
          setPendingWorkbookContext({
            file,
            result,
            requestId,
            scope: requestScope,
            overwriteAccepted: acceptOverwrite,
          });
          return;
        }
      }
      const result = await resolveManifestFileRows({
        file,
        editableExistingAndMockCases,
        existingProcedures,
        pendingCaseCount,
        procedureFieldContext,
        labId,
        studyId,
        fields,
        rowsPerPage,
        fetchSlidesByIdBatches,
        isCurrentUpload,
        excludedRowNumbers,
      });
      if (!isCurrentUpload()) return reportDroppedUpload(requestId, requestScope);
      if (!result) return;
      if (!isEmpty(result.errorMessages)) {
        setPendingCasePlanFix(result.correction);
        const duplicateRows = result.resolution.duplicateSlideRows;
        setPendingDuplicateUpload(
          duplicateRows?.length ? { file, rows: duplicateRows, requestId, scope: requestScope } : null
        );
        updateManifestDialogUrlState({ uploadedErrorMessages: result.errorMessages });
        return;
      }
      acceptResolvedManifestUpload(result.resolution);
    } catch (error) {
      if (!isCurrentUpload()) return reportDroppedUpload(requestId, requestScope);
      console.error('Error occurred while resolving manifest upload:', error);
      updateManifestDialogUrlState({
        uploadedErrorMessages: [
          error instanceof ManifestError ? error.message : 'Error occurred while fetching slides. Please try again',
        ],
      });
    } finally {
      if (requestId === uploadRequestId.current) {
        setResolvingUpload(null);
        if (manifestInputRef.current) {
          manifestInputRef.current.value = '';
        }
      }
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    closeFileMenuRef.current?.();
    const file = first(event.target.files);
    if (!file) return;
    if (sourcesLoading) {
      // Rows must resolve against the complete saved cases, so the chosen file waits for them instead of being refused.
      const requestId = ++uploadRequestId.current;
      setPendingWorkbookContext(null);
      setPendingOverwrite(null);
      setPendingCasePlanFix(null);
      setPendingDuplicateUpload(null);
      setOverwriteAccepted(false);
      setDeferredUpload({ file, requestId, scope: currentUploadScope.current });
      setResolvingUpload({ requestId, scope: currentUploadScope.current });
      if (manifestInputRef.current) manifestInputRef.current.value = '';
      return;
    }
    await importManifestFile(file);
  };

  useEffect(() => {
    if (!deferredUpload || sourcesLoading) return;
    setDeferredUpload(null);
    if (deferredUpload.requestId === uploadRequestId.current && deferredUpload.scope === currentUploadScope.current) {
      void importManifestFile(deferredUpload.file);
    } else {
      reportDroppedUpload(deferredUpload.requestId, deferredUpload.scope);
    }
  });

  // The context and overwrite decisions were already made before the file was resolved for the first time.
  const importUploadWithoutRows = (excludedRowNumbers: number[]) => {
    if (!activePendingDuplicateUpload || disableInput) return;
    void importManifestFile(activePendingDuplicateUpload.file, true, true, excludedRowNumbers);
  };

  const reconcileWorkbookContext = (match: boolean) => {
    if (!activeWorkbookContext || disableInput || !canIncludeAssigned) return;
    const { file, result } = activeWorkbookContext;
    if (match) {
      if (foreignContext) {
        if (!destinationLookup.data || destinationLookup.isFetching || destinationLookup.isError) return;
        setDestinationConfirmation({
          requestId: activeWorkbookContext.requestId,
          scope: uploadScope,
          destination: destinationLookup.data,
        });
        return;
      }
      onBeforeHandlingChange();
      updateManifestDialogUrlState(({ overrideCases: _previousOverride, ...previousState }) => ({
        ...previousState,
        executionMode: result.context.handling,
      }));
      currentUploadScope.current = JSON.stringify([
        open,
        labId,
        studyId,
        result.context.handling,
        canIncludeAssigned,
        canSwitchLabs,
        draftUploadScope,
      ]);
    }
    void importManifestFile(file, true, activeWorkbookContext.overwriteAccepted);
  };

  const resetDestinationQueries = async (destination: Pick<ManifestDestination, 'labId' | 'studyId'>) => {
    const filters: QueryFilters = {
      predicate: ({ queryKey }) => {
        const [owner, scope] = queryKey;
        if (owner === 'permissions' || owner === 'studies' || owner === 'cancerTypes')
          return scope === destination.labId;
        if (owner === 'procedures') return scope === destination.studyId;
        if (owner === 'stainsTypes')
          return get(scope, 'labId') === destination.labId && get(scope, 'studyId') === destination.studyId;
        if (
          (owner === 'pendingSlides' || owner === 'pendingSlidesCount' || owner === 'slides') &&
          typeof scope === 'string'
        )
          return new URLSearchParams(scope).get('labId') === destination.labId;
        return false;
      },
    };
    await queryClient.cancelQueries(filters);
    queryClient.removeQueries(filters);
  };

  const switchManifestDestination = async () => {
    if (!activeDestinationConfirmation || !activeWorkbookContext || !foreignContext) return;
    const { requestId, scope } = activeDestinationConfirmation;
    const { file } = activeWorkbookContext;
    const context = foreignContext;
    const isCurrent = () => requestId === uploadRequestId.current && scope === currentUploadScope.current;
    try {
      const destination = await loadManifestDestination({ ...context, currentLabId: labId, canSwitchLabs });
      if (!isCurrent()) return;
      await resetDestinationQueries(destination);
      if (!isCurrent()) return;
      dismissFileImport();
      navigate(
        getManifestDialogUrl({
          studyId: destination.studyId,
          labId: destination.labId,
          manifestState: { ...getDefaultManifestDialogUrlState(), executionMode: context.handling },
        }),
        {
          state: {
            ...navigationState,
            manifestDestination: {
              file,
              context,
              destination,
              original: destinationTransfer?.original || {
                url: `${location.pathname}${location.search}${location.hash}`,
                labId,
                studyId,
                state: location.state,
              },
            },
          } satisfies ManifestDestinationNavigationState,
        }
      );
    } catch (error) {
      if (!isCurrent()) return;
      setDestinationError({ scope, message: error instanceof Error ? error.message : 'Destination lookup failed.' });
      setDestinationConfirmation(null);
    }
  };

  const returnToOriginalPlan = async () => {
    const transfer = destinationTransfer;
    if (!transfer || isReturningToOriginal) return;
    const scope = currentUploadScope.current;
    setIsReturningToOriginal(true);
    try {
      await loadManifestDestination({ ...transfer.original, currentLabId: labId, canSwitchLabs });
      if (scope !== currentUploadScope.current) return;
      await resetDestinationQueries(transfer.original);
      if (scope !== currentUploadScope.current) return;
      dismissFileImport();
      navigate(transfer.original.url, { state: transfer.original.state });
    } catch (error) {
      if (scope === currentUploadScope.current)
        setDestinationError({ scope, message: error instanceof Error ? error.message : 'Destination lookup failed.' });
    } finally {
      setIsReturningToOriginal(false);
    }
  };

  useEffect(() => {
    const transfer = destinationTransfer;
    if (
      !transfer?.file ||
      transfer.file === claimedNavigationFile.current ||
      transfer.context.labId !== labId ||
      transfer.context.studyId !== studyId ||
      transfer.context.handling !== executionMode ||
      destinationSourcesLoading ||
      !canIncludeAssigned ||
      hasSourceError
    )
      return;
    claimedNavigationFile.current = transfer.file;
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: { ...navigationState, manifestDestination: { ...transfer, file: undefined } },
    });
    void importManifestFile(transfer.file, true);
  }, [
    destinationTransfer,
    labId,
    studyId,
    executionMode,
    destinationSourcesLoading,
    canIncludeAssigned,
    hasSourceError,
  ]);

  return {
    acceptResolvedManifestUpload,
    activeDestinationConfirmation,
    activePendingOverwrite,
    activeWorkbookContext,
    closeFileMenuRef,
    currentPlanSignature,
    currentUploadScope,
    destinationError,
    destinationLookup,
    destinationSourcesLoading,
    destinationTransfer,
    disableInput,
    dismissDestinationConfirmation: () => setDestinationConfirmation(null),
    dismissFileImport,
    exportedPlanSignature,
    foreignContext,
    handleFileChange,
    importManifestFile,
    importUploadWithoutRows,
    invalidateUpload,
    isResolvingUploadedManifest,
    isReturningToOriginal,
    manifestInputRef,
    overwriteAccepted,
    pendingCasePlanFix,
    pendingDuplicateRows: activePendingDuplicateUpload?.rows ?? null,
    reconcileWorkbookContext,
    returnToOriginalPlan,
    setOverwriteAccepted,
    switchManifestDestination,
    uploadRequestId,
    uploadScope,
  };
};
